import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import dayjs from 'dayjs';
import { OrgReportsApi, OrgCourseRegistrationRow } from '@/services/org-reports.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<OrgCourseRegistrationRow>();

const COLUMNS = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('location', { header: 'Location' }),
  columnHelper.accessor('phone', { header: 'Phone' }),
  columnHelper.accessor('gender', { header: 'Gender', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('course', { header: 'Course', cell: (ctx) => <Badge tone="blue">{ctx.getValue()}</Badge> }),
  columnHelper.accessor('createdAt', { header: 'Registered', cell: (ctx) => dayjs(ctx.getValue()).format('D MMM YYYY') }),
];

/**
 * Noorani Report — platform-wide Quran-course-registration leads, read-
 * only. Matches real legacy's `CourseRegistrationController` shape — not
 * school/org-scoped, same flat list for every admin tier (see
 * OrgReportsService.courseRegistrations()'s own doc comment).
 */
export function OrgCourseRegistrationsPage() {
  const [gender, setGender] = useState('');
  const [course, setCourse] = useState('');
  const dt = useDataTable({ defaultLimit: 20 });

  const filterParams = { gender: (gender || undefined) as 'Male' | 'Female' | undefined, course: course || undefined };

  const { data, isFetching } = useQuery({
    queryKey: ['org-course-registrations', dt.params, filterParams],
    queryFn: () => OrgReportsApi.courseRegistrations({ ...dt.params, ...filterParams }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Noorani Report"
        description="Quran-course registration leads from the public registration form."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name, location, phone, or course"
            className="w-72 pl-9"
          />
        </div>
        <Select className="!w-40" value={gender} onChange={(e) => setGender(e.target.value)}>
          <option value="">All genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </Select>
        <Input value={course} onChange={(e) => setCourse(e.target.value)} placeholder="Filter by exact course" className="!w-56" />
      </div>

      <DataTable columns={COLUMNS} result={data} state={dt} loading={isFetching} emptyMessage="No registrations found." getRowId={(row) => row.id} />
    </>
  );
}
